import React, { useEffect, useRef, useState } from 'react';
import { IndianRupee, TrendingUp, Users, Target, ArrowRight, Rocket, CheckCircle } from 'lucide-react';
import { idealCompanyData } from '../../data/mock';

const iconMap = {
  IndianRupee,
  TrendingUp,
  Users,
  Target
};

const IdealCompanySection = ({ onOpenCalendar }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--brand-dark)' }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--brand-primary)' }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--brand-primary)' }} />
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div 
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <IndianRupee size={18} />
            <span className="font-bold text-sm">{idealCompanyData.badge}</span>
          </div>
          
          <h2 
            className={`heading-1 mb-3 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'white' }}
          >
            {idealCompanyData.title}
          </h2>
          <p 
            className={`display-small mb-4 ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--brand-primary)' }}
          >
            {idealCompanyData.subtitle}
          </p>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}
            style={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {idealCompanyData.description}
          </p>
        </div>

        {/* Criteria Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
          {idealCompanyData.criteria.map((item, index) => {
            const Icon = iconMap[item.icon];
            return (
              <div
                key={index}
                className={`p-6 rounded-3xl transition-all duration-300 hover:scale-[1.02] ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ 
                  animationDelay: `${(index + 4) * 100}ms`,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--brand-primary)' }}
                  >
                    <Icon size={28} style={{ color: 'var(--brand-dark)' }} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2" style={{ color: 'white' }}>
                      {item.title}
                    </h3>
                    <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats Row */}
        <div 
          className={`grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-12 p-6 rounded-3xl ${
            isVisible ? 'animate-fadeInUp' : 'opacity-0'
          }`}
          style={{ 
            animationDelay: '800ms',
            background: 'rgba(211, 255, 98, 0.1)',
            border: '1px solid rgba(211, 255, 98, 0.2)'
          }}
        >
          {idealCompanyData.stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="display-small mb-1" style={{ color: 'var(--brand-primary)' }}>
                {stat.value}
              </div>
              <div className="body-small" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '900ms' }}>
          <button
            onClick={onOpenCalendar}
            className="btn-accent"
          >
            <Rocket size={20} />
            {idealCompanyData.cta}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default IdealCompanySection;
